import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Package, Star, Trash2, Edit, Plus, Store, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminLayout from "./AdminLayout";

export default function ProductsManagement() {
  const [products, setProducts] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [shopFilter, setShopFilter] = useState("all");
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const [productsRes, shopsRes] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/shops")
      ]);
      setProducts(productsRes.data);
      setShops(shopsRes.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (productId, productName) => {
    if (!window.confirm(`هل أنت متأكد من حذف المنتج: "${productName}" نهائياً؟`)) {
      return;
    }
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success("تم حذف المنتج بنجاح");
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleSave = async (productData) => {
    setSaving(true);
    try {
      if (productData.id) {
        await api.put(`/admin/products/${productData.id}`, productData);
        toast.success("تم تحديث بيانات المنتج بنجاح");
      } else {
        await api.post("/admin/products", productData);
        toast.success("تم إضافة المنتج بنجاح");
      }
      setIsDialogOpen(false);
      setEditingProduct(null);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = !search ||
      (p.name && p.name.toLowerCase().includes(search.toLowerCase())) ||
      (p.description && p.description.toLowerCase().includes(search.toLowerCase())) ||
      (p.shop_name && p.shop_name.toLowerCase().includes(search.toLowerCase()));
    
    const matchesShop = shopFilter === "all" || p.shop_id === shopFilter;
    return matchesSearch && matchesShop;
  });

  return (
    <AdminLayout title="إدارة المنتجات" subtitle="التحكم بكافة البضائع المعروضة، الأسعار، الخصومات وحالة التوفر">
      <Card className="border border-border/80 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">قائمة المنتجات</CardTitle>
              <CardDescription>إجمالي المنتجات المسجلة: {products.length}</CardDescription>
            </div>
            <Button onClick={() => { setEditingProduct(null); setIsDialogOpen(true); }} className="gap-2 shrink-0">
              <Plus className="w-4 h-4" />
              إضافة منتج جديد
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="بحث باسم المنتج أو وصفه أو المتجر..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-9"
              />
            </div>
            <Select value={shopFilter} onValueChange={setShopFilter}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="تصفية حسب المتجر" />
              </SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">كافة المتاجر</SelectItem>
                {shops.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2 md:hidden">
                {filteredProducts.map((product) => (
                  <article key={product.id} className="rounded-2xl border border-border bg-card p-3.5">
                    <div className="flex items-start gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Package className="h-4 w-4" /></div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-extrabold">{product.name || "-"}</h3>
                        <p className="mt-1 truncate text-xs text-muted-foreground">{product.shop_name || "متجر غير معروف"} · {product.category || "عام"}</p>
                        <p className="mt-1 text-xs font-bold">{Number(product.price || 0).toLocaleString()} ل.س{Number(product.discount_percent || 0) > 0 ? ` · خصم ${product.discount_percent}%` : ""}</p>
                      </div>
                      <span className={`shrink-0 text-[10px] font-bold ${product.in_stock ? "text-emerald-600" : "text-rose-600"}`}>{product.in_stock ? "متوفر" : "نفد"}</span>
                    </div>
                    <div className="mt-3 flex justify-end gap-2 border-t border-border pt-2">
                      <Button variant="outline" size="sm" className="h-9 gap-1 text-xs" onClick={() => { setEditingProduct(product); setIsDialogOpen(true); }}><Edit className="h-3.5 w-3.5" /> تعديل</Button>
                      <Button variant="outline" size="sm" className="h-9 gap-1 border-destructive/20 text-xs text-destructive" onClick={() => handleDelete(product.id, product.name)}><Trash2 className="h-3.5 w-3.5" /> حذف</Button>
                    </div>
                  </article>
                ))}
                {filteredProducts.length === 0 && <div className="rounded-2xl bg-muted p-8 text-center text-sm text-muted-foreground">لا توجد أي منتجات مطابقة للبحث الحالي</div>}
              </div>
              <div className="hidden overflow-hidden rounded-xl border md:block">
                <table className="w-full text-sm text-right">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="p-3.5 font-semibold">المنتج</th>
                      <th className="p-3.5 font-semibold">المتجر</th>
                      <th className="p-3.5 font-semibold">السعر</th>
                      <th className="p-3.5 font-semibold">التصنيف</th>
                      <th className="p-3.5 font-semibold">حالة المخزون</th>
                      <th className="p-3.5 font-semibold text-center">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-muted/30 transition-colors">
                        <td className="p-3.5 font-medium">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                              <Package className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground leading-tight">{product.name || "-"}</p>
                              <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{product.description || "بدون وصف"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3.5">
                          <span className="inline-flex items-center gap-1.5 text-xs text-foreground font-medium">
                            <Store className="w-3.5 h-3.5 text-muted-foreground" />
                            {product.shop_name || "متجر غير معروف"}
                          </span>
                        </td>
                        <td className="p-3.5">
                          <div className="flex flex-col">
                            <span className="font-mono font-bold text-xs text-foreground">
                              {Number(product.price || 0).toLocaleString()} ل.س
                            </span>
                            {Number(product.discount_percent || 0) > 0 && (
                              <span className="text-[10px] text-emerald-600 font-semibold">
                                خصم {product.discount_percent}%
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted font-medium">
                            {product.category || "عام"}
                          </span>
                        </td>
                        <td className="p-3.5">
                          {product.in_stock ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                              <CheckCircle2 className="w-3.5 h-3.5" /> متوفر
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-rose-600 font-medium">
                              <XCircle className="w-3.5 h-3.5" /> نفد المخزون
                            </span>
                          )}
                        </td>
                        <td className="p-3.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={() => { setEditingProduct(product); setIsDialogOpen(true); }}
                              title="تعديل المنتج"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(product.id, product.name)}
                              title="حذف المنتج"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredProducts.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground text-sm">
                          لا توجد أي منتجات مطابقة للبحث الحالي
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Product Modal Form */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "تعديل بيانات المنتج" : "إضافة منتج جديد"}</DialogTitle>
            <DialogDescription>
              {editingProduct ? "تحديث سعر المنتج، الخصم، أو توفره في المخزون" : "إضافة منتج جديد وربطه بالمتجر المناسب"}
            </DialogDescription>
          </DialogHeader>
          <ProductForm
            product={editingProduct}
            shops={shops}
            saving={saving}
            onSave={handleSave}
            onClose={() => { setIsDialogOpen(false); setEditingProduct(null); }}
          />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function ProductForm({ product, shops, saving, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    shop_id: "",
    category: "عام",
    description: "",
    discount_percent: 0,
    in_stock: true,
  });

  useEffect(() => {
    if (product) {
      setFormData({
        id: product.id,
        name: product.name || "",
        price: product.price ?? "",
        shop_id: product.shop_id || "",
        category: product.category || "عام",
        description: product.description || "",
        discount_percent: product.discount_percent ?? 0,
        in_stock: product.in_stock !== false,
      });
    } else {
      setFormData({
        name: "",
        price: "",
        shop_id: shops[0]?.id || "",
        category: "عام",
        description: "",
        discount_percent: 0,
        in_stock: true,
      });
    }
  }, [product, shops]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-2">
      <div>
        <Label htmlFor="p_name">اسم المنتج *</Label>
        <Input
          id="p_name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="مثال: شاورما دجاج سوبر"
          required
        />
      </div>
      <div>
        <Label htmlFor="p_shop">المتجر التابع له *</Label>
        <Select value={formData.shop_id} onValueChange={(v) => setFormData({ ...formData, shop_id: v })}>
          <SelectTrigger id="p_shop" dir="rtl">
            <SelectValue placeholder="اختر المتجر" />
          </SelectTrigger>
          <SelectContent dir="rtl">
            {shops.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="p_price">السعر (ل.س) *</Label>
          <Input
            id="p_price"
            type="number"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
            min={0}
            step={500}
            required
          />
        </div>
        <div>
          <Label htmlFor="p_discount">نسبة الخصم (%)</Label>
          <Input
            id="p_discount"
            type="number"
            value={formData.discount_percent}
            onChange={(e) => setFormData({ ...formData, discount_percent: Number(e.target.value) })}
            min={0}
            max={100}
          />
        </div>
      </div>
      <div>
        <Label htmlFor="p_desc">وصف وتفاصيل المنتج</Label>
        <Input
          id="p_desc"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="مكونات الوجبة، الحجم، أو أي تفاصيل..."
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="p_cat">التصنيف</Label>
          <Input
            id="p_cat"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            placeholder="عام، وجبات، حلويات..."
          />
        </div>
        <div>
          <Label htmlFor="p_stock">حالة التوفر</Label>
          <Select value={String(formData.in_stock)} onValueChange={(v) => setFormData({ ...formData, in_stock: v === "true" })}>
            <SelectTrigger id="p_stock" dir="rtl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent dir="rtl">
              <SelectItem value="true">متوفر في المخزون</SelectItem>
              <SelectItem value="false">غير متوفر حالياً</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t">
        <Button variant="outline" type="button" onClick={onClose} disabled={saving}>
          إلغاء
        </Button>
        <Button type="submit" disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
          {product ? "حفظ التعديلات" : "إضافة المنتج"}
        </Button>
      </div>
    </form>
  );
}
